import React from 'react';
import Styles from './Step7FinalSettings.module.scss';
import { FiTarget, FiZap, FiTrendingUp, FiUsers, FiDollarSign } from 'react-icons/fi';

const BUSINESS_GOALS = [
  { id: 'BRAND_AWARENESS', name: 'Brand Awareness', icon: FiTrendingUp },
  { id: 'LEAD_GENERATION', name: 'Lead Generation', icon: FiTarget },
  { id: 'CUSTOMER_ENGAGEMENT', name: 'Customer Engagement', icon: FiUsers },
  { id: 'SALES_CONVERSION', name: 'Sales & Conversion', icon: FiDollarSign },
  { id: 'THOUGHT_LEADERSHIP', name: 'Thought Leadership', icon: FiZap },
];

const Step7FinalSettings = ({ data, onUpdate, onNext, onPrevious, onSaveAndExit }) => {
  const {
    primaryBusinessGoal = 'BRAND_AWARENESS',
    creativityLevel = 0.7,
    customInstructions = '',
    additionalContext = '',
  } = data;

  const handleGoalSelect = (goalId) => {
    onUpdate({ primaryBusinessGoal: goalId });
  };

  const handleCreativityChange = (e) => {
    onUpdate({ creativityLevel: parseFloat(e.target.value) });
  };

  const getCreativityLabel = (value) => {
    if (value <= 0.3) return 'Conservative';
    if (value <= 0.5) return 'Balanced';
    if (value <= 0.7) return 'Creative';
    return 'Very Creative';
  };

  const handleFinish = () => {
    onNext();
  };

  return (
    <div className={Styles.container}>
      {/* Header */}
      <div className={Styles.header}>
        <h2 className={Styles.title}>Final Settings</h2>
        <p className={Styles.subtitle}>
          Configure AI behavior and set your primary objectives
        </p>
      </div>

      {/* Form Content */}
      <div className={Styles.formContent}>
        {/* Business Goal */}
        <div className={Styles.section}>
          <label className={Styles.label}>Primary Business Goal</label>
          <p className={Styles.helpText}>
            What's your main objective for content creation?
          </p>
          <div className={Styles.goalsGrid}>
            {BUSINESS_GOALS.map((goal) => {
              const Icon = goal.icon;
              const isSelected = primaryBusinessGoal === goal.id;

              return (
                <div
                  key={goal.id}
                  className={`${Styles.goalCard} ${isSelected ? Styles.selected : ''}`}
                  onClick={() => handleGoalSelect(goal.id)}
                >
                  <div
                    className={`${Styles.iconWrapper} ${isSelected ? Styles.iconActive : ''}`}
                  >
                    <Icon className={Styles.icon} />
                  </div>
                  <div className={Styles.goalName}>{goal.name}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Creativity Level */}
        <div className={Styles.section}>
          <label className={Styles.label}>AI Creativity Level</label>
          <p className={Styles.helpText}>
            Control how creative vs. conservative the AI should be
          </p>
          <div className={Styles.sliderContainer}>
            <div className={Styles.sliderWrapper}>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={creativityLevel}
                onChange={handleCreativityChange}
                className={Styles.slider}
              />
              <div className={Styles.sliderLabels}>
                <span>Conservative</span>
                <span>Balanced</span>
                <span>Creative</span>
                <span>Very Creative</span>
              </div>
            </div>
            <div className={Styles.sliderValue}>
              {getCreativityLabel(creativityLevel)}
            </div>
          </div>
          <div className={Styles.creativityInfo}>
            <p className={Styles.infoText}>
              <strong>Conservative:</strong> Stays close to your examples, minimal variation
            </p>
            <p className={Styles.infoText}>
              <strong>Creative:</strong> More variety and experimentation while maintaining your voice
            </p>
          </div>
        </div>

        {/* Custom Instructions */}
        <div className={Styles.inputGroup}>
          <label className={Styles.label}>Custom Instructions for AI</label>
          <p className={Styles.helpText}>
            Any specific rules or guidelines AI should follow when creating content
          </p>
          <textarea
            className={Styles.textarea}
            placeholder="e.g., Always include a call-to-action, Avoid industry jargon, Use short paragraphs..."
            value={customInstructions}
            onChange={(e) => onUpdate({ customInstructions: e.target.value })}
            rows={4}
          />
        </div>

        {/* Additional Context */}
        <div className={Styles.inputGroup}>
          <label className={Styles.label}>Additional Context (Optional)</label>
          <p className={Styles.helpText}>
            Anything else we should know about your brand or content needs?
          </p>
          <textarea
            className={Styles.textarea}
            placeholder="Any other details, preferences, or context that would help AI create better content for you..."
            value={additionalContext}
            onChange={(e) => onUpdate({ additionalContext: e.target.value })}
            rows={4}
          />
        </div>

        {/* Success Box */}
        <div className={Styles.successBox}>
          <div className={Styles.successIcon}>
            <FiZap />
          </div>
          <div className={Styles.successContent}>
            <div className={Styles.successTitle}>You're All Set!</div>
            <div className={Styles.successText}>
              Your Brand Intelligence profile will help AI create content that truly represents your brand voice, style, and objectives.
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className={Styles.footer}>
        <button className={Styles.previousBtn} onClick={onPrevious}>
          Previous
        </button>
        <button className={Styles.saveExitBtn} onClick={onSaveAndExit}>
          Save & Exit
        </button>
        <div className={Styles.stepIndicator}>Step 7 of 7</div>
        <button className={Styles.finishBtn} onClick={handleFinish}>
          Save & Finish
        </button>
      </div>
    </div>
  );
};

export default Step7FinalSettings;
